import type { ContentType, TestSource, ABTest, HypothesisVote, HypothesisType } from "@/types/ab-tests";

export function getContentTypeLabel(contentType: ContentType): string {
  if (contentType === "title") return "Title";
  if (contentType === "bullet-set") return "Bullet Set";
  const bulletMatch = /^bullet-(\d+)$/.exec(contentType);
  if (bulletMatch) return `Bullet ${bulletMatch[1]}`;
  return contentType;
}

/** A listing always offers at least this many bullet slots for testing, even if the product has fewer. */
export const MIN_TESTABLE_BULLETS = 5;

/**
 * The listing fields a test may target: Title always, plus Bullet 1..max(5, bulletCount).
 * Single source for every field selector so they cannot drift apart.
 */
export function getTestableFields(bulletCount: number): { value: ContentType; label: string }[] {
  const slots = Math.max(MIN_TESTABLE_BULLETS, bulletCount);
  const fields: { value: ContentType; label: string }[] = [{ value: "title", label: "Title" }];
  for (let i = 1; i <= slots; i++) {
    const value = `bullet-${i}` as ContentType;
    fields.push({ value, label: getContentTypeLabel(value) });
  }
  return fields;
}

/** Zero-based slot index of a `bullet-N` field, or null for any other field. */
export function getBulletIndex(contentType: ContentType): number | null {
  const match = /^bullet-(\d+)$/.exec(contentType);
  return match ? parseInt(match[1], 10) - 1 : null;
}

export function parseHeroAsinProductId(heroAsinId: string): number | null {
  const m = /^hero-(\d+)$/.exec(heroAsinId.trim());
  return m ? Number(m[1]) : null;
}

export function getTestSourceLabel(source?: TestSource | null): string {
  if (!source) return "\u2014";
  const labels: Record<TestSource, string> = {
    "ai-magic": "Signal AI Magic",
    "ai-format": "Format Treatment",
    "brand-signal": "Brand Signal",
    keyword: "Keyword Bank",
    landscape: "Landscape",
    "seo-insight": "SEO Insights",
    scorecard: "Amazon AI Score",
    "positioning-editor": "Positioning Editor",
    manual: "Manual Edit",
    upload: "Upload",
  };
  return labels[source] ?? "\u2014";
}

export function getWinningHypotheses(test: ABTest): HypothesisVote[] {
  if (!test.hypothesisMatrix || !test.outcome || test.outcome === "inconclusive") return [];
  const winner = test.outcome === "variant" ? "B" : "A";
  return test.hypothesisMatrix.filter(h => h.presentIn === winner);
}

export function buildAutoTestName(source: string | null, sourceDetail: string, appliedScorecardGaps?: string[]): string {
  let name: string;
  switch (source) {
    case "ai-magic":
      name = sourceDetail ? `Signal AI rewrite: ${sourceDetail}` : "Signal AI rewrite";
      break;
    case "brand-signal":
      name = sourceDetail ? `Brand signal: ${sourceDetail}` : "Brand signal";
      break;
    case "keyword":
      name = sourceDetail ? `Keyword: "${sourceDetail}"` : "Keyword test";
      break;
    case "seo-insight":
      name = sourceDetail ? `SEO insight: ${sourceDetail}` : "SEO insight";
      break;
    case "landscape":
      name = sourceDetail ? `Landscape: ${sourceDetail}` : "Landscape test";
      break;
    case "scorecard":
      name = appliedScorecardGaps && appliedScorecardGaps.length >= 2
        ? `Content gaps: ${appliedScorecardGaps.join(", ")}`
        : sourceDetail ? `Content gap: ${sourceDetail}` : "Content gap";
      break;
    case "ai-format":
      name = sourceDetail ? `Format: ${sourceDetail}` : "Format treatment";
      break;
    case "positioning-editor":
      name = sourceDetail ? `Positioning: ${sourceDetail}` : "Positioning edit";
      break;
    default:
      name = "Manual edit";
  }
  if (name.length > 255) {
    name = name.slice(0, 252) + "...";
  }
  return name;
}

export const HYPOTHESIS_TYPE_LABELS: Record<HypothesisType, string> = {
  "intent-shift": "Intent shift",
  "keyword-add": "Keyword add",
  "keyword-position": "Keyword position",
  "structure-change": "Structure change",
  "tone-shift": "Tone shift",
  "length-change": "Length change",
  "format-change": "Format change",
  "social-proof": "Social proof",
  "specificity-add": "Specificity add",
};
